import { useState } from 'react';

const services = [
  {
    id: 'sourcing',
    number: '01',
    icon: 'ri-search-2-line',
    color: 'bg-emerald-700',
    accentColor: 'border-emerald-600',
    tagColor: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    title: 'Strategic Sourcing & Supplier Intelligence',
    summary: 'Pinpointing and qualifying the world\'s most reliable, compliant, and collaborative supply partners for your specific technical needs.',
    items: [
      {
        label: 'Targeted Identification',
        detail: 'Pinpointing reputable, high-quality global sources that align with your specific technical requirements. We leverage an extensive international network spanning Europe, China, India, and North America to surface options that generic procurement channels simply cannot reach.',
      },
      {
        label: 'Rigorous Vetting',
        detail: 'Multi-stage screening of potential suppliers to isolate only the most viable, stable, and collaborative options. Our process evaluates financial stability, production capacity, quality systems, and long-term partnership potential.',
      },
      {
        label: 'Facility Pre-Qualification',
        detail: 'On-site and remote evaluation of facilities, product specifications, and the solicitation of physical samples and technical dossiers. We validate manufacturing environments against your internal standards and applicable regulatory frameworks before any commitment is made.',
      },
    ],
    image: 'https://public.readdy.ai/ai/img_res/aed1e136945af81ccbd2810ff569c07b.jpg',
  },
  {
    id: 'compliance',
    number: '02',
    icon: 'ri-shield-check-line',
    color: 'bg-teal-700',
    accentColor: 'border-teal-600',
    tagColor: 'bg-teal-50 text-teal-700 border-teal-100',
    title: 'Regulatory Compliance & Quality Assurance',
    summary: 'Ensuring every supplier, product, and process meets the exacting standards of global regulatory bodies — from FDA to Health Canada to EMA.',
    items: [
      {
        label: 'Compliance Auditing',
        detail: 'Detailed evaluation of product and facility alignment with global regulatory standards including FDA 21 CFR, Health Canada GMP, EMA guidelines, and ICH requirements. We identify gaps before they become liabilities.',
      },
      {
        label: 'Canadian DMF Representation',
        detail: 'Acting as a localized Drug Master File (DMF) agent for the Canadian market. We manage the preparation, submission, and ongoing maintenance of DMF filings with Health Canada on behalf of international manufacturers.',
      },
      {
        label: 'Audit Coordination',
        detail: 'Seamless management of both internal client audits and external regulatory inspections. We prepare suppliers for audits, coordinate logistics, provide translation support, and ensure all corrective actions are tracked and closed.',
      },
      {
        label: 'Documentation Management',
        detail: 'Comprehensive gathering and verification of all supporting technical and safety documentation — including CoA, CoC, stability data, impurity profiles, and regulatory dossiers — ensuring complete traceability for every shipment.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=pharmaceutical%20quality%20assurance%20laboratory%20scientist%20reviewing%20compliance%20documents%20regulatory%20standards%20clean%20professional%20environment%20white%20lab%20coat%20modern%20facility%20with%20green%20accents%20and%20stone%20tones&width=700&height=420&seq=svc-compliance-01&orientation=landscape',
  },
  {
    id: 'liaison',
    number: '03',
    icon: 'ri-user-voice-line',
    color: 'bg-stone-700',
    accentColor: 'border-stone-500',
    tagColor: 'bg-stone-100 text-stone-700 border-stone-200',
    title: 'Executive Liaison & Communication Shielding',
    summary: 'Acting as your trusted intermediary — protecting your time, confidentiality, and strategic position throughout every supplier interaction.',
    items: [
      {
        label: 'Strategic Intermediary',
        detail: 'We act as your primary filter, shielding you from excessive or "abusive" supplier communications and maintaining total confidentiality until you are ready to proceed. Your identity and strategic intent remain protected throughout the entire qualification process.',
      },
      {
        label: 'Cross-Border Coordination',
        detail: 'Bridging language barriers and managing complex time-zone logistics for 24/7 responsiveness. Our bilingual (EN/FR) team and multilingual network ensure nothing is lost in translation — whether negotiating in Mandarin, Hindi, or German.',
      },
      {
        label: 'Global Representation',
        detail: 'Providing professional representation for suppliers and coordinating high-stakes meetings, international site visits, and factory tours. We ensure your interests are represented with authority and precision at every touchpoint.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=professional%20executive%20business%20meeting%20international%20team%20around%20conference%20table%20global%20corporate%20environment%20clean%20modern%20office%20with%20warm%20stone%20tones%20and%20green%20plants%20representing%20cross-border%20collaboration&width=700&height=420&seq=svc-liaison-01&orientation=landscape',
  },
  {
    id: 'logistics',
    number: '04',
    icon: 'ri-truck-line',
    color: 'bg-emerald-800',
    accentColor: 'border-emerald-700',
    tagColor: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    title: 'Logistics & Contract Architecture',
    summary: 'Securing the best possible terms and managing the full physical journey of your goods — from factory floor to your facility.',
    items: [
      {
        label: 'Negotiation Excellence',
        detail: 'Securing optimal pricing and favorable supply conditions through expert contract architecture. We leverage deep market knowledge and supplier relationships to negotiate terms that protect your margins and ensure supply continuity.',
      },
      {
        label: 'End-to-End Logistics',
        detail: 'Managing the planning, transport, and customs clearance of goods across international borders. We coordinate temperature-controlled freight, IoT-enabled tracking, and full regulatory compliance at every border crossing.',
      },
      {
        label: 'Supply Chain Planning',
        detail: 'Coordinating production schedules and delivery requirements to ensure lean, just-in-time operations. We align supplier lead times with your production calendar to minimize inventory carrying costs while eliminating stockout risk.',
      },
    ],
    image: 'https://readdy.ai/api/search-image?query=international%20cargo%20logistics%20shipping%20containers%20port%20aerial%20view%20global%20supply%20chain%20management%20warm%20golden%20hour%20light%20industrial%20atmosphere%20professional%20freight%20transport%20network%20emerald%20and%20stone%20color%20palette&width=700&height=420&seq=svc-logistics-01&orientation=landscape',
  },
];

export default function ServicesDetail() {
  const [activeService, setActiveService] = useState(services[0].id);
  const active = services.find((s) => s.id === activeService) ?? services[0];

  return (
    <section className="w-full bg-white py-20 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Tab nav */}
        <div className="flex flex-wrap gap-2 mb-14 border-b border-stone-100 pb-6">
          {services.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveService(s.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeService === s.id
                  ? `${s.color} text-white`
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <i className={`${s.icon} text-base`} />
              <span className="hidden sm:inline">{s.title.split('&')[0].trim()}</span>
              <span className="sm:hidden">{s.number}</span>
            </button>
          ))}
        </div>

        {/* Active service detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left: text */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${active.color}`}>
                <i className={`${active.icon} text-white text-2xl`} />
              </div>
              <div>
                <span className="text-xs font-bold text-stone-400 tracking-widest uppercase">Service {active.number}</span>
                <h2
                  className="text-2xl md:text-3xl font-extrabold text-stone-900 leading-tight"
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                  {active.title}
                </h2>
              </div>
            </div>

            <p className="text-stone-600 text-base leading-relaxed mb-8 border-l-4 border-emerald-500 pl-5 italic">
              {active.summary}
            </p>

            <div className="space-y-5">
              {active.items.map((item, i) => (
                <div key={i} className={`rounded-lg border-l-4 ${active.accentColor} bg-stone-50 px-5 py-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-checkbox-circle-fill text-emerald-600 text-base" />
                    <h4 className="text-stone-800 font-bold text-sm">{item.label}</h4>
                  </div>
                  <p className="text-stone-500 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: image + tags */}
          <div className="flex flex-col gap-6">
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden">
              <img
                src={active.image}
                alt={active.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {active.items.map((item, i) => (
                <span
                  key={i}
                  className={`text-xs font-semibold border px-3 py-1 rounded-full ${active.tagColor}`}
                >
                  {item.label}
                </span>
              ))}
            </div>
            <div className="bg-stone-50 rounded-lg border border-stone-100 p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-information-line text-emerald-600" />
                <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Why it matters</span>
              </div>
              <p className="text-stone-600 text-sm leading-relaxed">
                In today&apos;s complex global supply environment, each of these capabilities directly reduces risk, protects margins, and ensures your supply chain remains resilient — regardless of geopolitical or regulatory shifts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
